/* eslint-disable react-hooks/exhaustive-deps */
import { Box, LinearProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RegisterStage, StatusCode } from '../../../api/enumerations';
import { registerPassword, sendTokenPassword } from '../../../api/register';
import { createAccountResetPassword } from '../../../api/resetPassword';
import { IconVisibility, PasswordIcon } from '../../../components/InputIcon';
import { WhiteCircularProgress } from '../../../components/UI/Typography';
import { validateEmail, validatePassword } from '../../../helpers';
import { setLocalStorageToken } from '../../../helpers/localStorage';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useSnackbar from '../../../hooks/useSnackbar';
import { Constants } from '../constants';
import { FilledButton, InputBox, StyledTextField } from './styles';

interface PasswordProps {
  accessToken: string;
  setPasswordToken: (value: string) => void;
  handleNextStage: (stage: RegisterStage) => void;
}

export default function Password({
  accessToken,
  setPasswordToken,
  handleNextStage,
}: PasswordProps): JSX.Element {
  const [initialLoading, setInitialLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [confirmationError, setConfirmationError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [sendToken, setSendToken] = useState(false);
  const [email, setEmail] = useState('');

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const navigateLogin = (): void => navigate('/login');

  const handleResetPassword = useCallback(async () => {
    if (!accessToken) {
      handleSnackbar('Algo deu errado, tente novamente.', true);
      navigateLogin();
      return;
    }

    try {
      const response = await createAccountResetPassword(accessToken);

      if (response.detail.status_code === StatusCode.ERROR) {
        handleSnackbar(
          response.detail.description || 'Solicite novamente o email.',
          true
        );
        setSendToken(true);
        return;
      }

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      if (response.data.token) {
        setPasswordToken(response.data.token);
        setToken(response.data.token);
        setLocalStorageToken(response.data.token);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      navigateLogin();
    } finally {
      setInitialLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    handleResetPassword();
  }, [handleResetPassword]);

  const handleSendToken = useCallback(async (): Promise<void> => {
    if (!validateEmail(email)) {
      handleSnackbar('Adicione um email válido.', true);
      return;
    }

    try {
      const response = await sendTokenPassword(email);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      handleSnackbar(
        'Email enviado com sucesso! Verifique sua caixa de entrada.',
        false
      );
      navigateLogin();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, [token, email]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      setLoading(true);

      if (!token) {
        handleSnackbar('Algo deu errado, tente novamente.', true);
        navigateLogin();
        return;
      }

      const passwordData = {
        password,
        password_confirmation: confirmation,
      };

      try {
        const response = await registerPassword(token, passwordData);

        if (response.detail.description)
          throw new Error(response.detail.description);

        if (response.detail.status_code !== StatusCode.OK)
          throw new Error('Algo deu errado, tente novamente.');

        handleNextStage(RegisterStage.AUTH_METHOD);
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        setLoading(false);
      }
    },
    [token, password, confirmation]
  );

  if (initialLoading) {
    return (
      <Box width="100%">
        <LinearProgress />
      </Box>
    );
  }

  return sendToken ? (
    <InputBox>
      <Typography m={2}>{Constants.expiredToken}</Typography>
      <StyledTextField
        required
        variant="standard"
        id="email"
        placeholder={Constants.typeEmail}
        error={passwordError}
        onChange={(e) => {
          setEmail(e.target.value);
        }}
      />
      <FilledButton onClick={handleSendToken}>
        {Constants.sendToken}
      </FilledButton>
    </InputBox>
  ) : (
    <InputBox component="form" id="set-password" onSubmit={handleSubmit}>
      <StyledTextField
        required
        type={showPassword ? 'text' : 'password'}
        variant="standard"
        helperText={password && Constants.passwordHelp}
        InputProps={{
          startAdornment: <PasswordIcon color="#8B8C8E" />,
          endAdornment: (
            <IconVisibility
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              color="#8B8C8E"
            />
          ),
        }}
        id="password"
        placeholder={Constants.password}
        error={passwordError}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          !validatePassword(e.target.value)
            ? setPasswordError(true)
            : setPasswordError(false);
        }}
      />
      <StyledTextField
        required
        type={showConfirmation ? 'text' : 'password'}
        variant="standard"
        helperText={confirmation && Constants.confirmHelp}
        InputProps={{
          startAdornment: <PasswordIcon color="#8B8C8E" />,
          endAdornment: (
            <IconVisibility
              showPassword={showConfirmation}
              setShowPassword={setShowConfirmation}
              color="#8B8C8E"
            />
          ),
        }}
        id="confirmation"
        placeholder={Constants.confirmPassword}
        error={confirmationError}
        value={confirmation}
        onChange={(e) => {
          setConfirmation(e.target.value);
          e.target.value !== password
            ? setConfirmationError(true)
            : setConfirmationError(false);
        }}
      />
      <FilledButton
        form="set-password"
        type="submit"
        disabled={passwordError || confirmationError || loading}
      >
        {loading ? <WhiteCircularProgress size={22} /> : Constants.confirm}
      </FilledButton>
    </InputBox>
  );
}
